import React, { useState } from 'react';
import { IconButton, type IconButtonProps, Variant } from '../IconButton/IconButton';
import { Button } from '../Button/Button';
import { SUPERFAVORITE_TILE_BUTTON_TEXT } from '../../helpers/tileHelper';
import { track } from '../../helpers/tracking';
import useIsLoggedIn from '@SharedScripts/Hooks/useIsLoggedIn';
import { getLoginUrl } from '@SharedScripts/UserData/Authentication';
import { isMobile, isTablet } from '@SharedScripts/Utils/DeviceClass';
import { LottieAnimation } from '../Animation/LazyLottieAnimation';

export interface GameActionsProps {
  className?: string;
  favourited?: boolean;
  favouritedAriaLabel?: string;
  hideFavouriteBackground?: boolean;
  primaryButtonCallback?: () => void;
  href?: string;
  linkAriaLabel?: string;
  playSize?: IconButtonProps<'play' | 'favourite'>['size'];
  favoriteSize?: IconButtonProps<'play' | 'favourite'>['size'];
  buttonEnabled?: boolean;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryCallback?: () => void;
  secondaryButtonHref?: string;
  secondaryButtonText?: string;
  tabIndex?: number;
  isTableClosed?: boolean;
  addToSearchInteractions?: () => void
  iconVariant?: Variant
}

export const GameActions = ({
  primaryButtonCallback,
  href,
  linkAriaLabel,
  favourited,
  favouritedAriaLabel,
  hideFavouriteBackground = false,
  className = '',
  playSize = 'medium',
  favoriteSize = 'medium',
  buttonEnabled = false,
  primaryButtonText = SUPERFAVORITE_TILE_BUTTON_TEXT,
  primaryButtonHref,
  secondaryButtonHref,
  secondaryCallback,
  secondaryButtonText,
  tabIndex = 0,
  isTableClosed = false,
  addToSearchInteractions,
  iconVariant
}: GameActionsProps) => {
  const isLoggedIn = useIsLoggedIn();
  const [favoriteHasBeenClicked, setFavoriteHasBeenClicked] = useState<boolean>(false);

  return (
    <div className={`inline-flex pointer-events-auto ${!favoriteHasBeenClicked ? 'gap-8' : ''} ${className}`}>
      {!buttonEnabled && <>
        {typeof primaryButtonCallback === 'function' && <div
          data-tracking-meta-data={JSON.stringify({
            action: !favourited ? 'add to favourites click' : 'remove from favourites click',
            label: !favourited ? 'add to favourites click' : 'remove from favourites click',
          })}
          onClick={track}
          onMouseEnter={() => {
            LottieAnimation.preload();
          }}
          onTouchStart={() => {
            LottieAnimation.preload();
          }}
        >
          {
            favoriteHasBeenClicked ? (
              <IconButton
                variant={'favorite-animation'}
                action={primaryButtonCallback}
                size={favoriteSize}
                ariaLabel={favouritedAriaLabel}
                checked={favourited}
                hideBackground={hideFavouriteBackground}
                tabIndex={tabIndex}
              />
            ) : (
              <IconButton
                variant={iconVariant ?? 'favorite-logo'}
                action={() => {
                  setFavoriteHasBeenClicked(true);
                  primaryButtonCallback();
                }}
                size={favoriteSize}
                ariaLabel={favouritedAriaLabel}
                checked={favourited}
                hideBackground={hideFavouriteBackground}
                tabIndex={tabIndex}
              />
            )
          }
        </div>}

        {href && !isTableClosed && (
          <div data-tracking-meta-data={ JSON.stringify({ action: 'game open', label: isMobile() || isTablet() ? 'mobile' : 'desktop' }) }
            onClick={(event) => {
              track(event);
              addToSearchInteractions?.();
            }}
          >
            <IconButton
              variant='play'
              href={isLoggedIn ? href : getLoginUrl({ successUrl: href })}
              size={playSize}
              ariaLabel={linkAriaLabel}
              as='a'
              tabIndex={tabIndex}
            />
          </div>
        )}
      </>
      }

      {buttonEnabled && <>
        {primaryButtonText && <div className='inline-flex'
          data-tracking-meta-data={JSON.stringify({ action: 'primary button click', label: primaryButtonText })}
          onClick={track}
        >
          <Button
            as={primaryButtonHref ? 'a' : 'button'}
            href={primaryButtonHref}
            text={primaryButtonText}
            variant='secondary'
            size='small'
            onClick={primaryButtonCallback}
            tabIndex={tabIndex}
            className='px-24 py-12 lg:px-40 lg:py-20 min-w-[119px] text-center content-center no-underline hover:no-underline active:no-underline'
          /></div>}

        {secondaryButtonText && <div className='inline-flex'
          data-tracking-meta-data={JSON.stringify({ action: 'secondary button click', label: secondaryButtonText })}
          onClick={track}
        >
          <Button
            as={secondaryButtonHref ? 'a' : 'button'}
            href={secondaryButtonHref}
            text={secondaryButtonText}
            variant='primary'
            size='small'
            onClick={secondaryCallback}
            tabIndex={tabIndex}
            className='px-24 py-12 lg:px-40 lg:py-20 min-w-[119px] text-center content-center no-underline hover:no-underline active:no-underline'
          /></div>}
      </>
      }
    </div>
  );
};
